import { useEffect, useMemo, useState } from "react";
import { Option } from "../../common/commonSelect";
import webservice_url from "../../webservice/Webservice_url";
import useApi from "../useApi";

interface DataTypes {
  department_id: string;
  department_code: string;
  department_name: string;
}

// ========= Admin PORTAL ===========

// Departments by multiple Program IDs
export function useDepartmentByProgramId(programIds: string[]) {
  const [dataList, setDataList] = useState<DataTypes[]>([]);

  // API
  const { callApi, loading, error } = useApi<any>("", {
    method: "POST",
    autoCall: false,
    handleGlobalError: false,
  });

  // Convert API data into dropdown options
  const options = useMemo<Option[]>(() => {
    if (!dataList?.length) {
      return [];
    }

    return dataList.map((d) => ({
      label: d?.department_name ?? "",
      value: d?.department_id ?? "",
    }));
  }, [dataList]);

  // Fetch departments for selected programs
  const fetchData = async () => {
    // No program selected
    if (!Array.isArray(programIds) || programIds.length === 0) {
      setDataList([]);
      return;
    }

    try {
      const payload = {
        programIds: programIds,
      };

      // Single POST request
      const res = await callApi(
        payload,
        webservice_url.get_departments,
      );

      // API response is { data: [...] }
      const data: DataTypes[] = Array.isArray(res?.data)
        ? res.data
        : [];

      // Remove duplicate departments using department_id
      const uniqueData: DataTypes[] = Array.from(
        new Map<string, DataTypes>(
          data.map((item) => [
            item.department_id,
            item,
          ]),
        ).values(),
      );

      setDataList(uniqueData);
    } catch (err: any) {
      console.error("Error fetching departments:", err);
      setDataList([]);
    }
  };

  // Stable dependency key for array
  const programKey = JSON.stringify(programIds ?? []);

  // Fetch whenever selected programs change
  useEffect(() => {
    if (!Array.isArray(programIds) || programIds.length === 0) {
      setDataList([]);
      return;
    }

    fetchData();

    // fetchData intentionally omitted
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programKey]);

  return {
    dataList,
    options,
    loading,
    error,
    fetchData,
  };
}


// ========= END Admin PORTAL ===========


// Departments by managerId
export function useDepartmentsByManagerId(
  managerId: string,
  programId: string,
) {
  const [dataList, setDataList] = useState<DataTypes[]>([]);

  // API
  const { callApi, loading } = useApi<any>(
    webservice_url.get_departments_by_managerId,
    {
      method: "POST",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const options = useMemo<Option[]>(() => {
    if (!dataList?.length) {
      return [];
    }

    return dataList.map((d) => ({
      label: d?.department_name ?? "",
      value: d?.department_id ?? "",
    }));
  }, [dataList]);

  // Fetch data
  const fetchData = async () => {
    try {
      const res = await callApi({
        manager_id: managerId ?? "",
        programId: programId ?? "",
      });

      setDataList(
        Array.isArray(res)
          ? res
          : (res?.data ?? []),
      );
    } catch (err: any) {
      console.error(err);
      setDataList([]);
    }
  };

  useEffect(() => {
    if (!managerId || !programId) {
      return;
    }

    fetchData();
  }, [managerId, programId]);

  return {
    dataList,
    options,
    loading,
  };
}


// ========= VERIFIER PORTAL ===========

export function useDepartmentByProgramId_Verifier(
  programId: string,
) {
  const [dataList, setDataList] = useState<DataTypes[]>([]);

  const { callApi, loading } = useApi<any>("", {
    method: "GET",
    autoCall: false,
    handleGlobalError: false,
  });

  const options = useMemo<Option[]>(() => {
    return dataList.map((d) => ({
      label: d.department_name,
      value: d.department_id,
    }));
  }, [dataList]);

  const fetchData = async () => {
    if (!programId) {
      setDataList([]);
      return;
    }

    try {
      const res = await callApi(
        undefined,
        `${webservice_url.fetchDepartment}?programId=${programId}`,
      );

      setDataList(
        Array.isArray(res)
          ? res
          : (res?.data ?? []),
      );
    } catch (err) {
      setDataList([]);
      console.error(err);
    }
  };

  return {
    dataList,
    options,
    loading,
    fetchData,
  };
}

// ========= END VERIFIER PORTAL ===========


// Departments by multiple Program IDs
export function useDepartmentByMultiProgramId(
  programId: string[],
) {
  const [dataList, setDataList] = useState<DataTypes[]>([]);

  // API
  const { callApi, loading, error } = useApi<any>(
    webservice_url.getDepartmentByMultiProgram,
    {
      method: "POST",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const options = useMemo<Option[]>(() => {
    if (!dataList?.length) {
      return [];
    }

    return dataList.map((d) => ({
      label: d?.department_name ?? "",
      value: d?.department_id ?? "",
    }));
  }, [dataList]);

  // Fetch data
  const fetchData = async () => {
    if (!Array.isArray(programId) || programId.length === 0) {
      setDataList([]);
      return;
    }

    try {
      const res = await callApi({
        programIds: programId,
      });

      const responseData: DataTypes[] = Array.isArray(res)
        ? res
        : (res?.data ?? []);

      // Remove duplicate departments
      const uniqueData = Array.from(
        new Map(
          responseData.map((item) => [item.department_id, item]),
        ).values(),
      );

      setDataList(uniqueData);
    } catch (err: any) {
      setDataList([]);
      console.error(err);
    }
  };

  const programKey = JSON.stringify(programId ?? []);

  useEffect(() => {
    if (!Array.isArray(programId) || programId.length === 0) {
      setDataList([]);
      return;
    }

    fetchData();

    // fetchData intentionally omitted
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programKey]);

  return {
    dataList,
    options,
    loading,
    error,
    fetchData,
  };
}